import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CreditCard, ShieldCheck, XCircle, Loader2, TestTube2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function PaymentTestSimulator() {
  const [sp] = useSearchParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const orderId = sp.get("order");
  const [payment, setPayment] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // This page is reached via a full-page window.location redirect (test payment
    // "gateway"), so AuthProvider remounts and re-fetches /auth/me from scratch.
    // Wait for that to resolve before deciding the user is logged out — otherwise
    // a still-valid session gets bounced to /giris while `user` is momentarily null.
    if (loading) return;
    if (!user) { nav(`/giris?next=${encodeURIComponent(window.location.pathname + window.location.search)}`); return; }
    api.get(`${V1}/payments/iyzico/order/${orderId}`)
      .then((r) => setPayment(r.data))
      .catch(() => toast.error("Sipariş bulunamadı"));
  }, [orderId, user, loading, nav]);

  const finish = async (outcome) => {
    setBusy(true);
    try {
      await api.post(`${V1}/payments/iyzico/test/confirm`, { order_id: orderId, outcome });
      window.location.href = `/odeme-sonuc?order=${orderId}&status=${outcome}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hata");
      setBusy(false);
    }
  };

  if (!payment) return <div className="max-w-lg mx-auto py-24 text-center text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="min-h-[70vh] bg-gradient-to-br from-primary/5 via-background to-accent/5 py-16 px-4" data-testid="payment-test-page">
      <div className="max-w-lg mx-auto">
        <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/30 text-xs font-medium">
          <TestTube2 className="h-3.5 w-3.5" /> TEST MODU — Gerçek ödeme alınmaz
        </div>

        <div className="bg-card rounded-2xl border border-border p-8 shadow-xl">
          {/* fake iyzico brand */}
          <div className="flex items-center gap-3 pb-4 border-b border-border">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-pink-500 grid place-content-center text-white font-bold">i</div>
            <div>
              <div className="font-display font-bold text-lg">iyzico</div>
              <div className="text-xs text-muted-foreground">SanoStudent güvenli ödeme (simülasyon)</div>
            </div>
            <ShieldCheck className="h-5 w-5 text-success ml-auto" />
          </div>

          <div className="py-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Sipariş</div>
            <div className="font-display font-bold text-xl mt-1">{payment.item_name || payment.code}</div>

            <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
              <div><div className="text-muted-foreground text-xs">Tür</div><div className="font-medium">{{ plan: "Abonelik", module: "Modül", credit_package: "Kredi Paketi", promote_exam: "Sınav Öne Çıkarma" }[payment.kind] || "Ürün"}</div></div>
              <div><div className="text-muted-foreground text-xs">Dönem</div><div className="font-medium">{payment.cycle === "monthly" ? "Aylık" : "Yıllık"}</div></div>
              <div><div className="text-muted-foreground text-xs">Sipariş No</div><div className="font-mono text-xs">{payment.id.slice(0, 16)}…</div></div>
              <div><div className="text-muted-foreground text-xs">Müşteri</div><div className="font-medium truncate">{user?.name}</div></div>
            </div>

            <div className="mt-6 p-4 rounded-lg bg-primary text-primary-foreground text-center">
              <div className="text-xs uppercase tracking-widest opacity-80">Toplam</div>
              <div className="font-display text-4xl font-bold mt-1">₺{payment.amount}</div>
            </div>

            <div className="mt-6 p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
              Bu simülasyon iyzico API anahtarları eklenmeden çalışan bir <b>test ekranı</b>dır. Aşağıdaki
              seçenekler gerçek bir kart işleminde iyzico&apos;nun döndüğü sonuçları simüle eder.
              Gerçek entegrasyon için <code className="bg-background px-1 py-0.5 rounded">IYZICO_API_KEY</code>
              ve <code className="bg-background px-1 py-0.5 rounded">IYZICO_SECRET_KEY</code> ekleyin.
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button onClick={() => finish("success")} disabled={busy} size="lg" data-testid="test-pay-success">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
              Başarılı Ödemeyi Simüle Et
            </Button>
            <Button onClick={() => finish("failure")} disabled={busy} size="lg" variant="outline" data-testid="test-pay-failure">
              <XCircle className="h-4 w-4" /> Başarısız Ödemeyi Simüle Et
            </Button>
            <Button onClick={() => nav(-1)} variant="ghost" size="sm">Vazgeç</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
